import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { makeListResponse } from '~/mocks/utils/listFixtures';
import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeReceiveStatusInfo } from '~/types/receive.types';
import type { TypePostStatusRequestedParams, TypePostStatusRequestedResponse } from '~/types/receive.types';

const fetchReceivingProgress = async (
  ownerCode: string,
  params: TypePostStatusRequestedParams
): Promise<TypePostStatusRequestedResponse> => {
  const { data } = await axiosInstance.get<TypePostStatusRequestedResponse>(
    `${API_ENDPOINT.RECEIVE.GET_RECEIVING_PROGRESS}/${ownerCode}/progress`,
    { params }
  );
  return data;
};

type TypeUseGetReceivingProgressQueryParams = {
  ownerCode: string;
  params: TypePostStatusRequestedParams;
  enabled?: boolean;
};

/**
 * [입고관리 > 입고현황] 입고현황 목록 조회 Query Hook
 */
export const useGetReceivingProgressQuery = ({
  ownerCode,
  params,
  enabled = true,
}: TypeUseGetReceivingProgressQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.receivingProgress(ownerCode, params),
    queryFn: () => fetchReceivingProgress(ownerCode, params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
};

// 5건 진행률·상태 분기를 모두 보이도록 시드 설계
// idx 1: PROCEEDING (입고중, 일부 입고됨)
// idx 2: COMPLETED (입고완료, 전량 입고)
// idx 3: FORCED_CLOSED (강제종료, 부분 입고 + 미입고)
// idx 4: PROCEEDING (입고중, 미시작 — receivedSkuCnt=0)
// idx 5: COMPLETED (KURLY_PICKUP 입고 방법 변형)
const PROGRESS_FIXTURES: Array<Omit<TypeReceiveStatusInfo, 'requestId' | 'requestCode'>> = [
  {
    status: 'PROCEEDING',
    estimatedArrivalDate: '2026-05-13',
    estimatedArrivalTime: '10:00~12:00',
    releaseCodeName: '컬리배차(직배송)',
    supplierCode: 'SU000004',
    supplierName: '테스트 화주사_첫번째 공급사',
    skuCnt: 5,
    qty: 120,
    receivedSkuCnt: 3,
    receivedQty: 72,
    receivedDate: null,
  },
  {
    status: 'COMPLETED',
    estimatedArrivalDate: '2026-05-12',
    estimatedArrivalTime: '14:00~16:00',
    releaseCodeName: '컬리배차(직배송)',
    supplierCode: 'SU000004',
    supplierName: '테스트 화주사_첫번째 공급사',
    skuCnt: 3,
    qty: 60,
    receivedSkuCnt: 3,
    receivedQty: 60,
    receivedDate: '2026-05-12T16:30:00',
  },
  {
    status: 'FORCED_CLOSED',
    estimatedArrivalDate: '2026-05-11',
    estimatedArrivalTime: '09:00~11:00',
    releaseCodeName: '밀크런(픽업)',
    supplierCode: 'SU000005',
    supplierName: '테스트 화주사_두번째 공급사',
    skuCnt: 4,
    qty: 80,
    receivedSkuCnt: 2,
    receivedQty: 40,
    receivedDate: '2026-05-11T18:00:00',
  },
  {
    status: 'PROCEEDING',
    estimatedArrivalDate: '2026-05-14',
    estimatedArrivalTime: '13:00~15:00',
    releaseCodeName: '컬리배차(픽업)',
    supplierCode: 'SU000004',
    supplierName: '테스트 화주사_첫번째 공급사',
    skuCnt: 2,
    qty: 40,
    receivedSkuCnt: 0,
    receivedQty: 0,
    receivedDate: null,
  },
  {
    status: 'COMPLETED',
    estimatedArrivalDate: '2026-05-10',
    estimatedArrivalTime: '11:00~13:00',
    releaseCodeName: '컬리배차(직배송)',
    supplierCode: 'SU000006',
    supplierName: '테스트 화주사_세번째 공급사',
    skuCnt: 1,
    qty: 20,
    receivedSkuCnt: 1,
    receivedQty: 20,
    receivedDate: '2026-05-10T15:00:00',
  },
];

const makeProgressItem = (index: number): TypeReceiveStatusInfo => {
  const fixture = PROGRESS_FIXTURES[(index - 1) % PROGRESS_FIXTURES.length];
  return {
    requestId: 9000 + index,
    requestCode: `OR20260513_${String(index).padStart(5, '0')}`,
    ...fixture,
  };
};

const buildProgressListResponse = (count: number): TypePostStatusRequestedResponse =>
  makeListResponse(count, makeProgressItem, 'receivingProgresses', {
    listView: 50,
    listTotal: count,
    cursor: 0,
  });

registerMockAPI<TypePostStatusRequestedResponse>({
  method: 'get',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/progress',
  label: '입고/입고현황/목록',
  scenarios: [
    { name: '정상 응답 — 5건 (입고중/완료/강제종료 혼합)', response: buildProgressListResponse(5) },
    { name: '정상 응답 — 0건 (빈 목록)', response: buildProgressListResponse(0) },
    {
      name: '잘못된 파라미터 (9001)',
      response: { error: { code: 9001, message: 'Error Occured By Not Valid Parameters' } },
      status: 400,
    },
  ],
});
